import React, { useState } from 'react';
import {
  IonContent,
  IonCard,
  IonCardContent,
  IonCardTitle,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon,
  IonText,
} from '@ionic/react';
import { mailOutline, lockClosedOutline, settingsOutline } from 'ionicons/icons';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { auth } from '@/services/UserAuthService';
import lookupDataService from '@/services/data/LookupDataService';
import ChangePasswordModal from '@/components/admin/user-preferences/ChangePasswordModal';
import SettingsModal from '@/components/common/SettingsModal';
import * as upa from '@/services/util/adapters/userPreferencesAdapters';
import { UserPreferencesResponse } from '@/types/response/userResponses';

const UserPreferences: React.FC = () => {
  usePageTitle('User Preferences');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showDefaultsModal, setShowDefaultsModal] = useState(false);
  const [currentEmail, setCurrentEmail] = useState<string>(auth.getUserDetails()?.email ?? '');

  const preferences: UserPreferencesResponse | null = lookupDataService.getUserPreferences();

  const handleEmailSaved = (newEmail: string) => {
    setCurrentEmail(newEmail);
  };

  const handlePreferencesSaved = () => {
    // Preferences saved — lookup data service will be refreshed on next contract load
    lookupDataService.reload();
  };

  const taskCards = [
    {
      id: 'email',
      icon: mailOutline,
      title: 'Email',
      description: 'Update your email address',
      onClick: () => setShowEmailModal(true),
    },
    {
      id: 'change-password',
      icon: lockClosedOutline,
      title: 'Change Password',
      description: 'Update your account password',
      onClick: () => setShowChangePasswordModal(true),
    },
    {
      id: 'defaults',
      icon: settingsOutline,
      title: 'Defaults',
      description: 'Configure dashboard and reporting preferences',
      onClick: () => setShowDefaultsModal(true),
    },
  ];

  return (
    <PageLayout>
      <IonContent>
        <div style={{ padding: '16px' }}>
          <IonGrid>
            <IonRow>
              {taskCards.map((card) => (
                <IonCol key={card.id} size="12" sizeMd="6" sizeLg="4">
                  <IonCard button onClick={card.onClick} style={{ cursor: 'pointer' }}>
                    <IonCardContent style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px' }}>
                      <IonIcon
                        icon={card.icon}
                        style={{ fontSize: '2rem', color: 'var(--ion-color-primary)', flexShrink: 0 }}
                      />
                      <div>
                        <IonCardTitle style={{ fontSize: '1rem', marginBottom: '4px' }}>{card.title}</IonCardTitle>
                        <IonText color="medium" style={{ fontSize: '0.875rem' }}>
                          {card.description}
                        </IonText>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              ))}
            </IonRow>
          </IonGrid>
        </div>

        <SettingsModal<upa.EmailForm>
          isOpen={showEmailModal}
          onDidDismiss={() => setShowEmailModal(false)}
          title="Email"
          fields={upa.emailFields}
          emptyData={upa.emailEmptyForm}
          initialData={upa.emailInitialData(currentEmail)}
          onSave={upa.saveEmail(handleEmailSaved)}
          successMessage="Email updated successfully"
          errorMessage="Failed to update email. Please try again."
        />

        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onDidDismiss={() => setShowChangePasswordModal(false)}
        />

        <SettingsModal<upa.DefaultsForm>
          isOpen={showDefaultsModal}
          onDidDismiss={() => setShowDefaultsModal(false)}
          title="Defaults"
          fields={upa.defaultsFields()}
          emptyData={upa.defaultsEmptyForm}
          initialData={upa.defaultsInitialData(preferences)}
          onSave={upa.saveDefaults}
          secondaryAction={upa.resetDefaultsAction}
          onSaved={handlePreferencesSaved}
          successMessage="Preferences saved successfully"
          errorMessage="Failed to save preferences. Please try again."
        />
      </IonContent>
    </PageLayout>
  );
};

export default UserPreferences;
